/**
 * ProgressBar Component
 * Dynamic progress bar based on prospect status
 */

import React, { useMemo } from 'react';

const styles = {
  container: {
    padding: '40px 0',
    marginBottom: '40px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '0.5px',
  },
  progressTrack: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    position: 'relative',
  },
  step: {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    backgroundColor: '#2A3444',
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  stepActive: {
    backgroundColor: '#0057FF',
    boxShadow: '0 0 12px rgba(0, 87, 255, 0.4)',
  },
  stepCompleted: {
    backgroundColor: '#00D4AA',
  },
  stepsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  stepBox: {
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    backgroundColor: 'transparent',
    transition: 'all 0.3s ease',
  },
  stepBoxActive: {
    borderColor: '#0057FF',
    backgroundColor: 'rgba(0, 87, 255, 0.1)',
  },
  stepBoxCompleted: {
    borderColor: '#00D4AA',
    backgroundColor: 'rgba(0, 212, 170, 0.1)',
  },
  stepLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#BBBEC7',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  stepLabelActive: {
    color: '#0057FF',
  },
  stepLabelCompleted: {
    color: '#00D4AA',
  },
  stepText: {
    fontSize: '14px',
    color: '#FFFFFF',
    fontWeight: '500',
  },
  stepCheckmark: {
    display: 'inline-block',
    marginRight: '8px',
    color: '#00D4AA',
  },
};

/**
 * ProgressBar Component
 * @param {Object} props
 * @param {Object} props.prospect - Prospect data
 */
function ProgressBar({ prospect }) {
  const steps = useMemo(() => {
    const allSteps = [
      { id: 'review', label: 'Initial Review', description: 'Your proposal is ready' },
      { id: 'discussion', label: 'Discussion', description: 'Schedule a call with our team' },
      { id: 'refinement', label: 'Refinement', description: 'Fine-tune the proposal' },
      { id: 'acceptance', label: 'Acceptance', description: 'Sign and approve' },
      { id: 'implementation', label: 'Implementation', description: 'We get started!' },
    ];

    // Determine current step based on prospect status
    const statusStepMap = {
      active: 0,
      pending: 1,
      review: 2,
      accepted: 4,
    };

    const currentStepIndex = statusStepMap[prospect?.status] || 0;

    return {
      steps: allSteps,
      currentStepIndex,
      completedSteps: currentStepIndex,
    };
  }, [prospect?.status]);

  const getProgressPercentage = () => {
    return ((steps.currentStepIndex + 1) / steps.steps.length) * 100;
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Your Journey with Talk Australia</h3>

      {/* Progress track */}
      <div style={styles.progressTrack}>
        {steps.steps.map((step, index) => (
          <div
            key={step.id}
            style={{
              ...styles.step,
              ...(index <= steps.completedSteps && styles.stepCompleted),
              ...(index === steps.currentStepIndex && styles.stepActive),
            }}
            aria-label={`${step.label} - ${index <= steps.completedSteps ? 'completed' : 'pending'}`}
          />
        ))}
      </div>

      {/* Step details */}
      <div style={styles.stepsContainer}>
        {steps.steps.map((step, index) => {
          const isActive = index === steps.currentStepIndex;
          const isCompleted = index < steps.currentStepIndex;

          return (
            <div
              key={step.id}
              style={{
                ...styles.stepBox,
                ...(isActive && styles.stepBoxActive),
                ...(isCompleted && styles.stepBoxCompleted),
              }}
            >
              <div
                style={{
                  ...styles.stepLabel,
                  ...(isActive && styles.stepLabelActive),
                  ...(isCompleted && styles.stepLabelCompleted),
                }}
              >
                {isCompleted && <span style={styles.stepCheckmark}>✓</span>}
                {isActive && '➜ '}{step.label}
              </div>
              <div style={styles.stepText}>{step.description}</div>
            </div>
          );
        })}
      </div>

      {/* Progress summary */}
      <div style={{ marginTop: '24px', fontSize: '14px', color: '#BBBEC7' }}>
        Progress: {Math.round(getProgressPercentage())}% complete
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default ProgressBar;
